import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  signal,
} from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Subject, takeUntil } from "rxjs";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { GlobalService } from "@bitwarden/common/services/global/global.service";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import {
  actionButtonOptions,
  institutionInputOptions,
} from "@bitwarden/web-vault/app/gloss/manage/manage-accounts/accounts-add-edit/component.options";
import { DialogWrapperComponent } from "@bitwarden/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { ManuallyAddInstitutionComponent } from "@bitwarden/web-vault/app/components/manually-add-institution/manually-add-institution.component";
import {
  Country,
  GlossInputOptions,
  isOfType,
  Origin,
} from "@bitwarden/web-vault/app/models/types/general-types";
import { SplitCategory } from "@bitwarden/web-vault/app/models/data/shared/split-category/split-category";
import { SplitClassificationType } from "@bitwarden/web-vault/app/models/types/split-classification-type";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import {
  AccountManualType,
  accountManualType,
  accountTypeTitleMap,
} from "@bitwarden/web-vault/app/models/types/account.types";

import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import { InstitutionAccountView } from "@bitwarden/web-vault/app/models/view/institution/institution-account.view";
import { InstitutionOriginEnum } from "@bitwarden/web-vault/app/models/types/institution.type";
import { InstitutionStoreModel } from "@bitwarden/web-vault/app/models/store/institution.store.model";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { DataPluginStoreService } from "@bitwarden/web-vault/app/services/store/data-plugin/data-plugin.store.service";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { CategoryView } from "@bitwarden/web-vault/app/models/view/category/category.view";
import { ClassificationView } from "@bitwarden/web-vault/app/models/view/classification/classification.view";
import {
  DefaultCurrencyPerLocation,
  UserLocationType,
  AllowedLocations,
  CurrencyType,
  AllowedCurrencies,
  getLocationCurrencyFromCountryCode,
} from "@bitwarden/web-vault/app/models/types/location-currency.types";
import { removeSpace } from "@bitwarden/web-vault/app/shared/utils/helper-string";
import { AccountStoreModel } from "@bitwarden/web-vault/app/models/store/account.store.model";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { renameDuplicateName } from "@bitwarden/web-vault/app/shared/utils/helper-new-view-name";
import { createOtherInstitution } from "@bitwarden/web-vault/app/shared/utils/helper.institution/default-institution-account";
import { OTHER } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { setAccountInstitutionLink } from "@bitwarden/web-vault/app/shared/utils/helper.account/institution-link.helper";
import { isSelectableCatalogInstitution } from "@bitwarden/web-vault/app/shared/utils/helper.institution/selectable-institution.helper";

// ISO 3166-1 alpha-2 reserves "ZZ" for "unspecified" — use it as the sentinel for the
// synthetic "Other" country option (never returned by the real countries API).
const OTHER_COUNTRY_CODE = "ZZ" as const;

@Component({
  selector: "app-account-add-edit",
  templateUrl: "account-add-edit.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountAddEditComponent implements OnInit, OnDestroy {
  private userStore = inject(UserStoreService);
  private dataPluginStore = inject(DataPluginStoreService);
  private appState = inject(AppStateStoreService);
  private i18nService = inject(I18nService);
  private globalService: GlobalService = inject(GlobalService);
  private dialog: MatDialog = inject(MatDialog);
  private dialogRef: MatDialogRef<AccountAddEditComponent> = inject(MatDialogRef);
  protected formBuilder: FormBuilder = inject(FormBuilder);

  private dialogFactory: DialogFactory;
  private destroy$ = new Subject<boolean>();

  baseCurrency: CurrencyType;
  form: FormGroup;
  accountName: string;
  /** User-set nickname for synced (non-manual) accounts; not overwritten by sync. */
  accountNickname: string;
  accountId: string;
  cat: string;
  categories: Array<CategoryView>;
  classifications: Array<ClassificationView>;
  institutions: Array<InstitutionView>;
  accountType: AccountManualType;
  private isAccountSaved: boolean = false;
  isEditMode = false;
  title: string;
  selectedClassifications: SplitClassificationType[] = [];
  selectedCategories: SplitCategory[] = [];
  selectedAccountProduct: InstitutionAccountView;

  // Reactive institution/country selection state (relocated from the old
  // institution-selection step so the choice happens inside the account form).
  selectedInstitution = signal<InstitutionView | undefined>(undefined);
  selectedCountry = signal<Country | null>(null);
  selectedInstitutionName = signal<string>("");
  institutionsIsRefreshing = signal<boolean>(false);
  currency = signal<CurrencyType | undefined>(undefined);
  accountNamePlaceHolder = signal<string>("");

  saveButtonOptions: ActionButton;
  institutionInputOptions: GlossInputOptions = institutionInputOptions;
  isStockOrOther: boolean;

  // Country list for the leading dropdown (mirrors the old CountriesComponent incl. wizard filter).
  countryViews = computed(() => {
    const countries = this.dataPluginStore.theCountryList.theCountryViews() ?? [];
    if (this.appState.wizard().isVisible === true) {
      return countries.filter(
        (country) => country.code === this.userStore.preferences.preferenceView()?.userLocation,
      );
    }
    return countries.concat({ name: OTHER, code: OTHER_COUNTRY_CODE });
  });

  countryNames = computed(() => this.countryViews().map((country) => country.name));

  countryCodes = computed(() =>
    Object.fromEntries(this.countryViews().map((country) => [country.name, country.code])),
  );

  selectedCountryName = computed(() => this.selectedCountry()?.name ?? "");

  // Unlinked manually-added institutions scoped to the selected country. Shared by
  // taggedManualInstitutions() and setInstitution(), like selectableAutoInstitutions().
  private readonly selectableManualInstitutions = computed(() =>
    this.userStore.institutions
      .institutionViews()
      .filter(
        (institution) =>
          institution.origin === InstitutionOriginEnum.manual &&
          institution.swift.countryCode === this.selectedCountry()?.code &&
          !institution.linkTo,
      ),
  );

  // Manually-added institutions scoped to the selected country (shown as tagged options).
  taggedManualInstitutions = computed(() =>
    this.selectableManualInstitutions().map((institution) => institution.name),
  );

  // Shared by institutionNames() and setInstitution() so a name always resolves to the option it
  // was picked from (otherwise a user's own manual "Opal" would resolve to the catalog's plugin
  // "Opal" first, linking the new account to the plugin institution).
  // theInstitutionViews() is null until the catalog loads (and after clear() or a failed fetch).
  private readonly selectableAutoInstitutions = computed(() =>
    (this.dataPluginStore.theInstitutionList.theInstitutionViews() ?? []).filter(
      isSelectableCatalogInstitution,
    ),
  );

  institutionNames = computed(() => {
    // Country "Other" has no institution list to fetch — don't leak the previously
    // selected (real) country's institutions into the dropdown while "Other" is active.
    if (this.selectedCountry()?.code === OTHER_COUNTRY_CODE) {
      return [OTHER];
    }
    const autoInstitutions = this.selectableAutoInstitutions().map(
      (institution) => institution.name,
    );
    // Dedupe: a manually-added institution can share a name with an auto one
    // (e.g. user created "ANZ" before the ANZ sync institution loaded).
    return Array.from(new Set(autoInstitutions.concat(this.taggedManualInstitutions())));
  });

  institutionPlaceholder = computed(() =>
    this.isStockOrOther ? OTHER : this.i18nService.t("institutionBankPlaceholder"),
  );

  // Account products available for the currently selected institution.
  accountTypes = computed(() => this.selectedInstitution()?.availableAccounts ?? []);

  accountManualTypeRef = accountManualType;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      account: AccountView;
      actionSucceeded: CallableFunction;
      delete: CallableFunction;
      institution: InstitutionView;
      closeDialog: CallableFunction;
      accountCurrency: CurrencyType;
      backClicked: CallableFunction;
      isWizard: boolean;
      accountType: AccountManualType;
      isFromGuide: boolean;
      /** Skip the post-create balance dialog (embedded-picker creation). */
      skipBalance?: boolean;
      /** Invoked with the persisted account right after creation. */
      onAccountCreated?: (account: AccountView) => void;
    },
  ) {
    this.isEditMode = !!this.data.account;
    this.accountType = this.data.accountType;
    this.isStockOrOther =
      this.accountType === accountManualType.stock || this.accountType === accountManualType.other;
    this.dialogFactory = new DialogFactory();

    const buttonText = this.data.isWizard || this.isEditMode ? "save" : "create";
    this.saveButtonOptions = new ActionButton({
      ...actionButtonOptions,
      text: this.i18nService.t(buttonText),
      onClick: this.submitNext.bind(this),
    });

    effect(() => {
      this.categories = this.userStore.categories.categoryViewsSorted();
      this.classifications = this.userStore.classifications.classificationViews();
      this.institutions = this.userStore.institutions.institutionViews();
    });
  }

  async ngOnInit() {
    const preference = this.userStore.preferences.preferenceView();
    this.baseCurrency = preference.coreCurrency;

    await this.setForm();
    this.initializeForm();
  }

  async setForm() {
    if (this.isEditMode) {
      await this.setFormToUpdate();
    } else {
      await this.setFormToCreate();
    }
  }

  setFormToUpdate() {
    this.title = this.i18nService.t("editAccount");
    this.institutionInputOptions = {
      ...this.institutionInputOptions,
      value: this.data.account.c_institution.name,
    };
    this.selectedInstitution.set(this.data.account.c_institution);
    this.selectedInstitutionName.set(this.data.account.c_institution?.name ?? "");
    this.selectedAccountProduct = this.data.account.c_institutionAccountType;
    // Show the institution/product name in the form's name field, not the user-set nickname.
    this.accountName = this.data.account.name;
    // Synced (non-manual) accounts can carry a user nickname; manual accounts don't.
    this.accountNickname =
      this.data.account.origin !== Origin.manual ? this.data.account.nickname : null;
    this.selectedCategories = this.data.account.defaultCategories;
    this.selectedClassifications = this.data.account.defaultClassifications;
    this.currency.set(this.data.account.currency);
  }

  async setFormToCreate() {
    this.title = this.i18nService.t(accountTypeTitleMap[this.accountType] ?? "addNewAccount");

    // app-currencies shows the location's default currency when none is passed in, and its
    // dropdown no longer emits on init for a pre-seeded value — so record that default here
    // (before the first await/render) or submit would fall back to baseCurrency instead.
    const userLocation = this.userStore.preferences.preferenceView()?.userLocation;
    if (isOfType<UserLocationType>(userLocation, AllowedLocations)) {
      this.currency.set(DefaultCurrencyPerLocation[userLocation]);
    }

    this.initializeCountry();

    // Pre-load institutions for the initial country, then pre-select "Other" for stock/other.
    if (this.isStockOrOther) {
      await this.setCountry(this.selectedCountry());
      this.selectOtherInstitution();
    } else if (this.selectedCountry()) {
      await this.setCountry(this.selectedCountry());
    }
  }

  onBackClicked() {
    if (this.data.closeDialog && this.data.backClicked) {
      this.data.closeDialog(true);
      this.data.backClicked();
    } else {
      /*TODO tell user to start over instead of saying something went wrong!!*/
      this.globalService.showWarningMessage("warning", "somethingWentWrong");
    }
  }

  cancel() {
    if (this.data.closeDialog) {
      this.data.closeDialog(true);
    } else {
      this.dialogRef.close();
    }
  }

  // ----- country + institution selection (relocated from institution-selection) -----

  private initializeCountry(): void {
    const code = this.userStore.preferences.preferenceView().userLocation as UserLocationType;
    const match = this.dataPluginStore.theCountryList
      .theCountryViews()
      ?.find((c) => c.code === code);
    this.selectedCountry.set(match ?? null);
  }

  onCountrySelected(countryName: string): void {
    const country = this.countryViews().find((c) => c.name === countryName);
    if (country) {
      void this.setCountry(country as Country);
    }
  }

  async setCountry(country: Country | null): Promise<void> {
    if (!country) {
      return;
    }
    this.selectedCountry.set(country);

    if (country.code === OTHER_COUNTRY_CODE) {
      this.selectOtherCountryDefaults();
      return;
    }

    if (!this.isStockOrOther) {
      this.setInstitution("");
    }

    if (!this.dataPluginStore.theInstitutionList.countryCode().includes(country.code)) {
      this.institutionsIsRefreshing.set(true);
      try {
        await this.dataPluginStore.theInstitutionList.fetch({ countryCode: country.code });
        if (this.isStockOrOther) {
          this.selectOtherInstitution();
        }
      } catch {
        this.globalService.showErrorMessage("error", "institutionSelectionFailed");
      } finally {
        this.institutionsIsRefreshing.set(false);
      }
    } else if (this.isStockOrOther) {
      this.selectOtherInstitution();
    }
  }

  setInstitution(institutionName: string): void {
    // Country "Other" only ever resolves to the synthesized "Other" institution — ignore
    // whatever name was clicked (it can only be a stale institution from the previously
    // fetched country, see institutionNames()) rather than looking it up below.
    if (this.selectedCountry()?.code === OTHER_COUNTRY_CODE) {
      // "Other" is the dropdown's only option, so it can be clicked again while selected: apply the
      // defaults only after a clear, or they'd reset a currency the user has changed since.
      if (!this.selectedInstitution()) {
        this.selectOtherCountryDefaults();
      }
      return;
    }

    this.selectedInstitutionName.set(institutionName ?? "");

    const autoInstitutions = this.selectableAutoInstitutions();
    const manualInstitutions = this.selectableManualInstitutions();
    const institution = autoInstitutions
      .concat(manualInstitutions)
      .find((i) => i.name === institutionName);

    // The account product, name placeholder and default currency belong to the institution — reset
    // them only when it changes (re-select a different bank, clear, or country switch): the dropdown
    // emits on every click, including on the selected option.
    if (this.selectedInstitution()?.id !== institution?.id) {
      this.selectedAccountProduct = undefined;
      if (institution) {
        this.accountNamePlaceHolder.set(`My ${institution.name} Account`);
        this.setDefaultCurrency(institution);
      }
    }

    this.selectedInstitution.set(institution);
    this.form?.get("selectedInstitution")?.setValue(institution ?? null);
  }

  // Clearing the institution also clears the account product, which then re-disables
  // it (it's only selectable once a valid institution is chosen).
  clearInstitution(): void {
    this.selectedInstitutionName.set("");
    this.selectedInstitution.set(undefined);
    this.selectedAccountProduct = undefined;
    this.accountNamePlaceHolder.set("");
    this.form?.get("selectedInstitution")?.setValue(null);
  }

  private setDefaultCurrency(institution: InstitutionView): void {
    this.currency.set(getLocationCurrencyFromCountryCode(institution.swift.countryCode));
  }

  private selectOtherInstitution(): void {
    const other = this.institutionNames().find((name) => name === OTHER);
    if (other) {
      this.setInstitution(other);
    }
  }

  // Country "Other" has no real institutions to fetch — synthesize the same "Other"
  // institution/product used elsewhere in this form, and default currency to Core Currency.
  private selectOtherCountryDefaults(): void {
    const institution = createOtherInstitution(
      this.userStore.preferences.preferenceView()?.userLocation ?? "AU",
    );
    this.selectedInstitution.set(institution);
    this.selectedInstitutionName.set(institution.name);
    this.form?.get("selectedInstitution")?.setValue(institution);
    this.selectedAccountProduct = institution.availableAccounts[0];
    this.accountNamePlaceHolder.set(`My ${institution.name} Account`);
    this.currency.set(this.baseCurrency);
  }

  addInstitution(): void {
    const country = this.selectedCountry();
    // "Other" is a dropdown-only sentinel (see selectOtherCountryDefaults) - it isn't a real
    // country and must never reach the dialog, or the Country field displays a value its own
    // picker can never reproduce (countryViews() has no "Other" entry). Resolve it to the same
    // real country selectOtherCountryDefaults() persists under.
    const userLocation = this.userStore.preferences.preferenceView()?.userLocation ?? "AU";
    const dialogRef = this.dialog.open(DialogWrapperComponent, {
      width: "400px",
      data: {
        title: "manuallyAddInstitution",
        showBackButton: false,
        component: ManuallyAddInstitutionComponent,
        componentInputs: {
          selectedCountry:
            country?.code === OTHER_COUNTRY_CODE
              ? this.countryViews().find((c) => c.code === userLocation)
              : country,
        },
      },
      disableClose: true,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        void this.onAddManualInstoClose(result);
      });
  }

  private async onAddManualInstoClose(addedInsto: InstitutionStoreModel): Promise<void> {
    if (addedInsto && addedInsto.name) {
      const cc = addedInsto.swt.cc;
      // The country list is null until it loads and may not contain cc; the code alone is enough
      // to select the institution (a miss would leave "Other" selected, see #5019).
      const institutionCountry = this.dataPluginStore.theCountryList
        .theCountryViews()
        ?.find((country) => country.code === cc) ?? { name: cc, code: cc };
      await this.setCountry(institutionCountry);
      this.setInstitution(addedInsto.name);
    }
  }

  // ----- form -----

  private getEmptyFormControls(): any {
    const { origin } = this.data.account ?? { origin: "manual" };
    const isDisabled = origin !== Origin.manual;

    return {
      accountName: [{ value: "", disabled: isDisabled }, Validators.required],
      selectedCategory: [{ value: null }],
      selectedClassification: [{ value: null }],
      selectedInstitution: [{ value: this.selectedInstitution(), disabled: isDisabled }],
      selectedAccountType: null,
      categories: null,
      classifications: null,
    };
  }

  private filledFormControls(): any {
    const {
      origin,
      name,
      defaultClassifications,
      defaultCategories,
      c_institution,
      c_institutionAccountType,
    } = this.data.account;
    const isDisabled = origin !== Origin.manual;
    return {
      accountName: [{ value: name, disabled: isDisabled }, Validators.required],
      selectedCategory: defaultCategories,
      selectedClassification: defaultClassifications,
      selectedInstitution: [{ value: c_institution, disabled: isDisabled }],
      selectedAccountType: c_institutionAccountType,
      categories: null,
      classifications: null,
    };
  }

  private getFormControls() {
    if (this.isEditMode) {
      return this.filledFormControls();
    } else {
      return this.getEmptyFormControls();
    }
  }

  initializeForm() {
    const formControls = this.getFormControls();
    this.form = this.formBuilder.group(formControls);

    this.categories.forEach((cat) => {
      this.form.addControl(`category_${cat.id}`, new FormControl(0));
    });

    this.classifications.forEach((cat) => {
      this.form.addControl(`classification_${cat.id}`, new FormControl(0));
    });
  }

  accountNameSet(name: string) {
    this.accountName = name;
  }

  accountNicknameSet(name: string) {
    this.accountNickname = name;
  }

  async beforeSubmit() {
    this.setEmptyNameToPlaceHolder();
    try {
      const isStock = this.accountType === accountManualType.stock;
      if (!this.isAccountSaved) {
        const firstSave = this?.data?.account?.isSyncInVault() ?? true;
        this.data.account = await this.saveAccount(firstSave, !isStock);
        this.isAccountSaved = true;
      } else if (isStock) {
        this.data.account = await this.saveAccount(false, true);
      }
      return true;
    } catch (e) {
      if (e.message === "AccountAlreadyExist") {
        this.globalService.throwBookExistsError();
      } else {
        this.globalService.showErrorMessage("errorOccurred", e);
      }
      return false;
    }
  }

  setEmptyNameToPlaceHolder() {
    if (!this.accountName || this.accountName === "") {
      if (this.accountNamePlaceHolder()) {
        this.accountName = this.accountNamePlaceHolder();
      } else {
        this.globalService.showErrorMessage("errorOccurred", "accountNameEmpty");
        this.saveButtonOptions.enableButton(true);
        return;
      }
    }
  }

  async submitNext() {
    // Institution is now chosen inside this form; guard before saving.
    if (this.isStockOrOther && !this.selectedInstitution()) {
      this.selectOtherInstitution();
    }

    // saveAccount() dereferences selectedInstitution().id, so it must be set by now.
    // For stock/other the implicit "Other" institution can be missing (fetch failed or
    // returned empty) — bail with an error instead of crashing on undefined.
    if (!this.selectedInstitution()) {
      if (this.isStockOrOther) {
        this.globalService.showErrorMessage("error", "institutionSelectionFailed");
      } else {
        this.globalService.showWarningMessage("warning", "institutionSelectionInfo");
      }
      this.saveButtonOptions.enableButton(true);
      return;
    }

    // For stock/other accounts, if no account product selected, default to "Other"
    if (this.isStockOrOther && !this.selectedAccountProduct) {
      this.selectOtherAccountProduct();
    }

    if (!this.selectedAccountProduct && !this.isStockOrOther) {
      this.globalService.showErrorMessage("errorOccurred", "accountTypeEmpty");
      this.saveButtonOptions.enableButton(true);
      return;
    }

    if (await this.beforeSubmit()) {
      if (!this.isEditMode) {
        this.data.actionSucceeded("createdSuccessfully");
        this.data.onAccountCreated?.(this.data.account);
      } else {
        this.data.actionSucceeded("updatedSuccessfully");
      }

      this.navigateToNextStep(this.data.accountType);
      this.saveButtonOptions.enableButton(true);
    } else {
      this.saveButtonOptions.enableButton(true);
    }
  }

  private selectOtherAccountProduct(): void {
    const otherAccountProduct = this.accountTypes().find((acc) => acc.name === OTHER);
    if (otherAccountProduct) {
      this.selectedAccountProduct = otherAccountProduct;
    }
  }

  navigateToNextStep(accountType: AccountManualType) {
    if (Object.keys(accountManualType).includes(accountType)) {
      this.data.closeDialog();
      if (!this.data.isWizard && !this.data.skipBalance) {
        this.openAccountBalanceDialog(this.data.account);
      }
    } else {
      throw new Error("Invalid account type");
    }
  }

  nameAlreadyExisted(accountName: string): boolean {
    const existingBooks = this.userStore.enrichAccounts.collection();
    return existingBooks.some((b) => removeSpace(b.name) === removeSpace(accountName));
  }

  private openAccountBalanceDialog(accountView: AccountView) {
    this.dialogFactory.addBalance(
      accountView,
      this.actionSucceeded.bind(this),
      this.data.isFromGuide,
    );
  }

  private actionSucceeded() {
    this.data.closeDialog(true);
  }

  private async createInstitution(institution: InstitutionView) {
    /** todo check if this is still needed. from previous discussion, this was for future proofing but not currently in use **/
    institution.availableAccounts.forEach((acc) => {
      if (acc.id === this.selectedAccountProduct.id) {
        acc.interestRates.forEach((ir) => {
          ir.symbol = this.currency();
        });
      }
    });

    const updatedInstitution = await this.userStore.institutions.save(institution);
    return new InstitutionView(updatedInstitution as InstitutionStoreModel);
  }

  private async saveAccount(isNew: boolean = false, skipDuplicateCheck: boolean = false) {
    if (isNew && !skipDuplicateCheck && this.nameAlreadyExisted(this.accountName)) {
      throw new Error("AccountAlreadyExist");
    }
    let account: AccountView;
    if (isNew) {
      account = new AccountView();
      account.type = this.accountType; // todo @alex map the account type to basiq type
      let institution = this.selectedInstitution();
      if (institution && !institution.isSyncInVault()) {
        institution = await this.createInstitution(institution);
        this.selectedInstitution.set(institution);
        // note: this returns the correct institution account type,
        // but compare to the previous one, this doesn't have interest rates in its name
        // find out why if needed
        this.selectedAccountProduct = institution.availableAccounts.find((acc) => {
          return acc.id === this.selectedAccountProduct.id;
        });
      }
    } else {
      account = this.data.account.clone();
    }

    account.name = renameDuplicateName(this.userStore.accounts.accountViews(), this.accountName);
    account.currency = this.currency() ?? this.baseCurrency;
    account.origin = Origin.manual;
    account.source = Origin.manual;

    setAccountInstitutionLink(account, {
      institutionId: this.selectedInstitution().id,
      institutionAccountId: this.selectedAccountProduct ? this.selectedAccountProduct.id : null,
    });

    const updatedModel = await this.userStore.accounts.save(account);
    return new AccountView(updatedModel as AccountStoreModel);
  }

  async deleteAccount() {
    // eslint-disable-next-line
    console.warn("This function as not been tested. originalBook has been removed ");
    this.data.delete(this.data.account);
  }

  selectCurrency(currency: string) {
    if (isOfType<CurrencyType>(currency, AllowedCurrencies)) {
      this.currency.set(currency);
    } else {
      this.globalService.showErrorMessage("errorOccurred", "invalidCurrency");
      return;
    }
  }

  // todo: same code in account-details
  onSelectAccountType(selected: InstitutionAccountView) {
    this.selectedAccountProduct = selected;
    this.accountNamePlaceHolder.set(`My ${selected.name}`);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
